import { httpClient, ApiError } from "./http-client";

// ─── Degradasi (QC-F11 / PF-10 bagian 5) ───────────────────────────────────────

/**
 * Anak tangga yang sedang dipijak store. Nilainya milik backend
 * (`service.DegradationStage`); daftar lengkap ada di sana, bukan disalin di
 * sini — kunci yang belum dikenal tetap dirender dengan gaya netral alih-alih
 * ditolak, supaya tahap baru dari backend tidak membuat banner rusak.
 */
export type DegradationStage =
  | "healthy"
  | "quota_warning"
  | "payg"
  | "reserve"
  | "restricted"
  | "suspended"
  | "expired"
  | (string & {});

export interface Degradation {
  stage: DegradationStage;
  /** Satu kalimat, tahapnya. */
  headline: string;
  /** Apa artinya bagi pengiriman. */
  impact: string;
  /** Apa yang bisa dilakukan pengguna. Tidak pernah kosong dari backend. */
  nextStep: string;
  /** Disebut terpisah dari `stillWorks` — dua daftar sengaja tidak digabung. */
  stopped: string[];
  stillWorks: string[];
  quotaPercent: number;
}

/** GET /api/v1/billing/degradation?store_id=xxx */
export async function getDegradation(storeId: string): Promise<Degradation> {
  const res = await httpClient.get<{
    stage: string;
    headline: string;
    impact: string;
    next_step: string;
    stopped: string[];
    still_works: string[];
    quota_percent: number;
  }>(`/api/v1/billing/degradation?store_id=${storeId}`);
  if (!res.success || !res.data) {
    throw new ApiError(res.error || "Gagal membaca tahap degradasi", 500);
  }
  return {
    stage: res.data.stage,
    headline: res.data.headline,
    impact: res.data.impact,
    nextStep: res.data.next_step,
    stopped: res.data.stopped ?? [],
    stillWorks: res.data.still_works ?? [],
    quotaPercent: res.data.quota_percent ?? 0,
  };
}

// ─── Batas paket & nomor efektif (QC-F10 / PF-02 · PF-08) ──────────────────────

/** Sisi mana yang mengikat batas nomor — menentukan jalan keluar yang ditawarkan. */
export type NumberLimitBoundBy = "plan" | "meta" | (string & {});

export interface PlanLimits {
  plan: string;
  whatsappNumbers: number;
  whatsappNumbersUsed: number;
  /** Yang lebih ketat antara batas paket dan plafon portfolio Meta. */
  whatsappNumbersEffective: number;
  whatsappNumbersBoundBy: NumberLimitBoundBy;
}

/** GET /api/v1/billing/plan?store_id=xxx */
export async function getPlanLimits(storeId: string): Promise<PlanLimits> {
  const res = await httpClient.get<{
    plan: string;
    whatsapp_numbers: number;
    whatsapp_numbers_used?: number;
    whatsapp_numbers_effective?: number;
    whatsapp_numbers_bound_by?: string;
  }>(`/api/v1/billing/plan?store_id=${storeId}`);
  if (!res.success || !res.data) {
    throw new ApiError(res.error || "Gagal membaca batas paket", 500);
  }
  return {
    plan: res.data.plan,
    whatsappNumbers: res.data.whatsapp_numbers,
    whatsappNumbersUsed: res.data.whatsapp_numbers_used ?? 0,
    whatsappNumbersEffective: res.data.whatsapp_numbers_effective ?? res.data.whatsapp_numbers,
    whatsappNumbersBoundBy: res.data.whatsapp_numbers_bound_by ?? "plan",
  };
}
